import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  accountApi,
  balanceApi,
  type AccountView,
  type BalanceOverview,
  type BalanceTransactionView,
} from '../api/client';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  HelpIcon,
  Input,
  LoadingState,
  PageHeader,
  PageLayout,
  Section,
  Table,
} from '../components';
import { useI18n, type TranslationKey } from '../i18n';
import { useSession } from '../auth/session-context';
import { formatDateTime, formatMoney, formatSignedMoney } from '../format';

/**
 * Guthaben (09_billing_payments.md).
 *
 * Die Plattform arbeitet mit einem Prepaid-Guthaben. Aufgeladen wird
 * ausschliesslich Guthaben - es gibt hier keine Minutenpakete, keine
 * Rufnummern- oder Channel-Pakete und nichts anderes zu kaufen. Was an
 * Leistungen anfaellt, wird intern von diesem Guthaben abgezogen und
 * erscheint in der Historie.
 *
 * V1 kennt ausschliesslich Krypto-Zahlungen - von Kartenzahlung ist deshalb
 * nirgends die Rede.
 */
export function BalancePage(): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [account, setAccount] = useState<AccountView | null>(null);
  const [balance, setBalance] = useState<BalanceOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [betrag, setBetrag] = useState('');
  const [zahlungsfehler, setZahlungsfehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(async () => {
    setError(null);
    try {
      const [konto, guthaben] = await Promise.all([accountApi.get(), balanceApi.overview()]);
      setAccount(konto.account);
      setBalance(guthaben);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    }
  }, [handleApiError, tError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const aufladen = async (): Promise<void> => {
    setZahlungsfehler(null);
    setLaeuft(true);
    try {
      // Der Mindestbetrag wird serverseitig geprueft. Die Anzeige hier ist
      // eine Hilfe, keine Pruefung.
      await balanceApi.topUp(betrag.replace(',', '.'));
      await laden();
      setBetrag('');
    } catch (caught) {
      handleApiError(caught);
      setZahlungsfehler(
        caught instanceof ApiError ? tError(caught.code, caught.details) : tError(undefined),
      );
    } finally {
      setLaeuft(false);
    }
  };

  const mindestbetrag = balance ? formatMoney(balance.minTopUpAmount) : null;

  return (
    <PageLayout width="mittel">
      <PageHeader title={t('balance.title')} />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {!balance && !error ? <LoadingState /> : null}

      {balance ? (
        <Card>
          <Section title={t('balance.current')}>
            <p className="kennzahl-wert">{formatMoney(balance.balance)}</p>
            <dl className="facts">
              {account ? (
                <>
                  <dt>
                    {t('balance.pricePerMinute')}
                    <HelpIcon
                      text={t('balance.help.pricePerMinute')}
                      label={t('balance.pricePerMinute')}
                    />
                  </dt>
                  <dd>{formatMoney(account.pricePerMinute, 3)}</dd>
                </>
              ) : null}
              <dt>{t('balance.minimum')}</dt>
              <dd>{mindestbetrag}</dd>
            </dl>
          </Section>
        </Card>
      ) : null}

      {balance ? (
        <Card>
          <Section title={t('balance.topUp')} description={t('balance.topUpText')}>
            {zahlungsfehler ? <Alert tone="error">{zahlungsfehler}</Alert> : null}

            <Field
              label={t('balance.amount')}
              hint={t('balance.minimumHint', { amount: mindestbetrag ?? '' })}
            >
              <Input
                type="text"
                value={betrag}
                onChange={setBetrag}
                placeholder={balance.minTopUpAmount.replace(/\.?0+$/, '')}
              />
            </Field>

            <Button
              onClick={() => void aufladen()}
              disabled={betrag.trim() === '' || laeuft}
            >
              {t('balance.payWithCrypto')}
            </Button>
          </Section>
        </Card>
      ) : null}

      {balance ? (
        <Card>
          <Section title={t('balance.history')}>
            {balance.transactions.length === 0 ? (
              <EmptyState title={t('balance.historyEmpty')} />
            ) : (
              <Table
                label={t('balance.history')}
                headers={[
                  t('balance.date'),
                  t('balance.type'),
                  t('balance.amountColumn'),
                  t('balance.balanceAfter'),
                  t('balance.note'),
                ]}
              >
                {balance.transactions.map((buchung) => (
                  <HistoryRow key={buchung.id} buchung={buchung} />
                ))}
              </Table>
            )}
          </Section>
        </Card>
      ) : null}
    </PageLayout>
  );
}

function HistoryRow({ buchung }: { buchung: BalanceTransactionView }): JSX.Element {
  const { t } = useI18n();

  return (
    <tr>
      <td>{formatDateTime(buchung.createdAt)}</td>
      {/* Nie der technische Typname - immer der uebersetzte Text. */}
      <td>{t(('balance.type.' + buchung.type) as TranslationKey)}</td>
      <td>{formatSignedMoney(buchung.amount)}</td>
      <td>{formatMoney(buchung.balanceAfter)}</td>
      <td>{buchung.note ?? '-'}</td>
    </tr>
  );
}
